import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useNavigate } from 'react-router-dom';
import SyntaxHighlighter from "react-syntax-highlighter";
import { qtcreatorDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import WinampTOC from "./WinampTOC";
import SegmentDateClock from './SegmentDateClock';
import { cleanMarkdownFormatting, createId, countWords } from './utils';
import "./Home.css";
import "./fonts.css";
import "./Channel.css";
import "./Animation.css";
import "./AboutMe.css";
import "./BlogPost.css";

const getTextContent = (children) => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(getTextContent).join('');
  if (children?.props?.children) return getTextContent(children.props.children);
  return '';
};

const TableElement = (props) => {
  const centeredStyle = {
    textAlign: "center",
    verticalAlign: "middle",
    paddingLeft: "1vh",
    paddingRight: "1vh",
    fontSize: "1.75vh",
    border: "1px solid #555", // Add border for better visibility
    padding: "8px"
  };

  return <td style={centeredStyle}>{props.children}</td>;
};

const TableHeader = (props) => {
  const centeredStyle = {
    textAlign: "center",
    verticalAlign: "middle",
    paddingLeft: "1vh",
    paddingRight: "1vh",
    background: "rgba(50, 50, 50, 0.8)", // Make it more visible
    fontSize: "2vh",
    fontWeight: "bold",
    border: "1px solid #777",
    padding: "10px"
  };

  return <th style={centeredStyle}>{props.children}</th>; // Use <th> not <td>
};

const CenteredTable = (props) => {
  const tableStyle = {
    margin: "20px auto", // Center the table and add some margin
    borderCollapse: "collapse", // Remove gaps between cells
    backgroundColor: "rgba(0, 0, 0, 0.3)", // Semi-transparent background
    borderRadius: "8px",
    overflow: "hidden"
  };

  const centeredStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "20px 0"
  };

  return (
    <div style={centeredStyle}>
      <table style={tableStyle}>{props.children}</table>
    </div>
  );
};

const CustomImage = ({ 
  alt, 
  src, 
  title, 
  windowGradientStart,
  windowGradientEnd,
  windowBorder,
  minimizeButton,
  maximizeButton,
  closeButton
}) => {
  const getFilename = (url) => {
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('/');
    const filename = parts[parts.length - 1];
    return decodeURIComponent(filename);
  };
  
  const filename = getFilename(src);
  
  const windowStyle = {
    background: '#ffffff3f',
    border: `1px solid ${windowBorder}`,
    borderRadius: '8px 8px 0 0',
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
    display: 'inline-block',
    overflow: 'hidden'
  };
  
  const titleBarStyle = {
    background: `linear-gradient(to bottom, ${windowGradientStart}, ${windowGradientEnd})`,
    color: 'white',
    fontFamily: "'Tahoma', sans-serif",
    fontSize: '1.25vh',
    fontWeight: 'bold',
    padding: '2px 4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };
  
  const buttonBaseStyle = {
    width: '1.25vh',
    height: '1.25vh',
    marginLeft: '2px',
    border: '1px solid #fff',
    borderRadius: '0.5vh'
  };

  return (
    <div className="winxp-window-container">
      <div style={windowStyle}>
        <div style={titleBarStyle}>
          <div className="winxp-title">
            <img src="/favicon.ico" alt="favicon" className="winxp-favicon" />
            <span className="winxp-filename">{filename}</span>
          </div>
          <div className="winxp-buttons">
            <span style={{
              ...buttonBaseStyle,
              background: minimizeButton
            }}></span>
            <span style={{
              ...buttonBaseStyle,
              background: maximizeButton
            }}></span>
            <span style={{
              ...buttonBaseStyle,
              background: closeButton
            }}></span>
          </div>
        </div>
        <div className="winxp-content">
          <img
            alt={alt}
            src={src}
            title={title}
          />
        </div>
      </div>
    </div>
  );
};

const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const codeRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);
  
  // Extract language from the className if it exists
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : 'text';
  
  // Determine if this is a long code block
  const lines = String(children).split('\n');
  const isLong = lines.length > 20;
  
  useEffect(() => {
    if (codeRef.current) {
      setIsScrollable(codeRef.current.scrollHeight > 200);
    }
  }, [children]);
  
  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (inline) {
    // For inline code (single backtick)
    return <code className="inline-code" {...props}>{children}</code>;
  }

  // For code blocks (triple backticks)
  return (
    <div className={`code-block ${isLong ? 'long-code' : ''} ${isScrollable ? 'scrollable' : ''}`}>
      {isLong && (
        <button className="code-toggle" onClick={toggleExpand}>
          {isExpanded ? 'Collapse Snippet' : 'Expand Snippet'}
        </button>
      )}
      <div className={`code-content ${isExpanded ? 'expanded' : ''}`} ref={codeRef}>
      <SyntaxHighlighter
        language={language}
        style={qtcreatorDark}
        wrapLongLines={false} // Change to false
        customStyle={{
          padding: '25px',
          maxWidth: '100%',
          overflowX: 'auto' // Add horizontal scrolling
        }}
        tabSize={2}
      >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

const getPostUrl = (post) => {
  const cleanPath = post.filename.replace(/^[^-]+-[\d]{4}-[\d]{2}-[\d]{2}-/, "");
  return `/${post.channel}/${cleanPath}`;
};

const BlogPostRender = ({ blogPost, prevPost, nextPost }) => {
  const [wordCount, setWordCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setWordCount(countWords(blogPost.content));
  }, [blogPost.content]);

  useEffect(() => {
    function adjustColumnHeight() {
      const leftCell = document.querySelector(".summary-container");
      const rightCell = document.querySelector(".right-cell");

      if (leftCell && rightCell) {
        rightCell.style.height = leftCell.offsetHeight + "px";
      }
    }

    adjustColumnHeight();
    window.addEventListener("resize", adjustColumnHeight);

    return () => {
      window.removeEventListener("resize", adjustColumnHeight);
    };
  }, []);

  const components = {
    code: CodeBlock,
    img: (props) => (
      <CustomImage 
        {...props}
        windowGradientStart={blogPost.windowGradientStart}
        windowGradientEnd={blogPost.windowGradientEnd}
        windowBorder={blogPost.windowBorder}
        minimizeButton={blogPost.minimizeButton}
        maximizeButton={blogPost.maximizeButton}
        closeButton={blogPost.closeButton}
      />
    ),
    table: CenteredTable,
    td: TableElement,
    th: TableHeader,
    tr: ({ node, ...props }) => <tr {...props} />,
    thead: ({ node, ...props }) => <thead {...props} />,
    tbody: ({ node, ...props }) => <tbody {...props} />,
    h1: ({ children, ...props }) => {
      const textContent = getTextContent(children);
      const cleanText = cleanMarkdownFormatting(textContent);
      const id = createId(cleanText);
      return <h1 id={id} {...props}>{children}</h1>;
    },
    h2: ({ children, ...props }) => {
      const textContent = getTextContent(children);
      const cleanText = cleanMarkdownFormatting(textContent);
      const id = createId(cleanText);
      return <h2 id={id} {...props}>{children}</h2>;
    },
    h3: ({ children, ...props }) => {
      const textContent = getTextContent(children);
      const cleanText = cleanMarkdownFormatting(textContent);
      const id = createId(cleanText);
      return <h3 id={id} {...props}>{children}</h3>;
    },
  };

  return (
    <div className="fade-in-blogpost">
      <div className="markdown-content">
        <div className="blog-title-selected">{blogPost.title}</div>
        <div className="horizontal-line" />
        <div className="grid-container">
          <div className="grid-item left-cell">
            <div className="summary-container">
              <div className="blog-tagline-selected">{blogPost.tagline}</div>
              {blogPost.tags.length > 0 && (
                <div className="blog-post-tags">
                  <ul className="blog-post-tag-grid">
                    {blogPost.tags.map((tag, index) => (
                      <li key={index} className="blog-post-tag">
                        {tag}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="blog-word-count-centered">Word count: {wordCount}</div>
            </div>
          </div>
          <div className="grid-item right-cell">
            <WinampTOC markdownContent={blogPost.content} />
          </div>
        </div>

        <div className="horizontal-line" />

        <ReactMarkdown
          components={components}
          rehypePlugins={[rehypeRaw]}
          remarkPlugins={[remarkGfm]}
        >
          {blogPost.content}
        </ReactMarkdown>
        <div className="horizontal-line"></div>
        <div className="blog-post-footer-nav">
          <button
            className="blog-nav-btn"
            onClick={() => navigate(getPostUrl(prevPost))}
            style={{ visibility: prevPost ? 'visible' : 'hidden' }}
          >
            ← {prevPost?.title}
          </button>
          <SegmentDateClock date={blogPost.date} />
          <button
            className="blog-nav-btn"
            onClick={() => navigate(getPostUrl(nextPost))}
            style={{ visibility: nextPost ? 'visible' : 'hidden' }}
          >
            {nextPost?.title} →
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogPostRender;